import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { LayoutDashboard, Filter, Megaphone, GraduationCap, ArrowRight } from "lucide-react";
import { EASE, Reveal, SectionTag } from "./motion";
import { scrollToSection } from "../lib/config";

const SCREENS = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    src: "/hub/dashboard.webp",
    caption: "Indicadores comerciais em tempo real: leads, agendamentos, comparecimentos e vendas fechadas.",
  },
  {
    id: "funil",
    label: "Funil de Vendas",
    icon: Filter,
    src: "/hub/funil.webp",
    caption: "CRM visual: acompanhe cada lead entre etapas, do primeiro contato ao fechamento.",
  },
  {
    id: "anuncios",
    label: "Anúncios Meta",
    icon: Megaphone,
    src: "/hub/anuncios.webp",
    caption: "Investimento, CPL, ROAS e evolução diária das campanhas conectadas direto ao CRM.",
  },
  {
    id: "academy",
    label: "Pulse Academy",
    icon: GraduationCap,
    src: "/hub/academy.webp",
    caption: "Trilhas de treinamento para a equipe dentro da própria plataforma, com progresso e certificação.",
  },
];

const DURATION = 5200;

export default function PulseHub() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduce = useReducedMotion();
  const secRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: secRef, offset: ["start end", "end start"] });
  const frameY = useTransform(scrollYProgress, [0, 1], [48, -48]);

  useEffect(() => {
    if (reduce || paused) return;
    const t = setTimeout(() => setActive((a) => (a + 1) % SCREENS.length), DURATION);
    return () => clearTimeout(t);
  }, [active, paused, reduce]);

  const screen = SCREENS[active];

  return (
    <section id="hub" ref={secRef} data-testid="hub-section" className="bg-bone text-graphite py-28 lg:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <Reveal>
            <SectionTag dark={false}>Tecnologia proprietária</SectionTag>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-ink leading-[1.12]" data-testid="hub-title">
              Pulse Hub. <span className="text-gold-deep">O cérebro da operação.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base sm:text-lg text-graphite/70 leading-relaxed">
              CRM, funil de vendas, automações, agente de IA, agenda e indicadores —
              tudo em uma única plataforma construída para clínicas.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-12 flex gap-2 overflow-x-auto pb-2 -mx-6 px-6 lg:mx-0 lg:px-0" data-testid="hub-tabs">
            {SCREENS.map((s, i) => (
              <button
                key={s.id}
                data-testid={`hub-tab-${s.id}`}
                onClick={() => setActive(i)}
                className={`relative shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 border overflow-hidden ${
                  i === active
                    ? "bg-ink text-gold border-ink"
                    : "bg-white text-graphite/70 border-graphite/15 hover:border-gold"
                }`}
              >
                <s.icon size={15} />
                {s.label}
                {i === active && !reduce && (
                  <motion.span
                    key={`bar-${active}-${paused}`}
                    className="absolute bottom-0 left-0 h-[3px] bg-gold"
                    initial={{ width: "0%" }}
                    animate={{ width: paused ? "0%" : "100%" }}
                    transition={{ duration: DURATION / 1000, ease: "linear" }}
                  />
                )}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <motion.div
            style={reduce ? {} : { y: frameY }}
            className="mt-10 max-w-5xl mx-auto relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            data-testid="hub-screen-frame"
          >
            <div className="absolute -inset-8 bg-gold/15 blur-[45px] lg:blur-[90px] rounded-full pointer-events-none" />

            <div className="relative rounded-2xl overflow-hidden border border-ink/20 shadow-[0_50px_120px_-30px_rgba(9,11,14,0.55)]">
              <div className="bg-ink px-5 py-3.5 flex items-center justify-between gap-4">
                <div className="flex gap-1.5 shrink-0">
                  <span className="w-2.5 h-2.5 rounded-full bg-bone/20" />
                  <span className="w-2.5 h-2.5 rounded-full bg-bone/20" />
                  <span className="w-2.5 h-2.5 rounded-full bg-gold" />
                </div>
                <span className="font-display font-extrabold tracking-[0.22em] text-sm sm:text-base text-bone whitespace-nowrap">
                  PULSE <span className="text-gold">HUB</span>
                </span>
                <span className="hidden md:block text-[10px] uppercase tracking-[0.28em] text-bone/40 whitespace-nowrap">
                  Gestão | Conhecimento | Resultados
                </span>
              </div>

              <div className="relative bg-ink aspect-[2000/1071]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={screen.id}
                    src={screen.src}
                    alt={`Tela real do Pulse Hub — ${screen.label}`}
                    data-testid="hub-screen-image"
                    initial={reduce ? false : { opacity: 0, x: 64, scale: 1.015 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={reduce ? undefined : { opacity: 0, x: -48 }}
                    transition={{ duration: 0.6, ease: EASE }}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </AnimatePresence>
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.p
                key={screen.id}
                data-testid="hub-caption"
                initial={reduce ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="mt-6 text-center text-sm sm:text-base text-graphite/70 max-w-xl mx-auto"
              >
                {screen.caption}
              </motion.p>
            </AnimatePresence>
          </motion.div>
        </Reveal>

        <Reveal delay={0.25} className="mt-12 text-center">
          <button
            data-testid="hub-cta"
            onClick={() => scrollToSection("#diagnostico")}
            className="group inline-flex items-center gap-3 bg-ink text-gold font-display font-bold px-8 py-4 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300"
          >
            Conheça o Pulse Hub
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}
